<template>
  <div class="planetarium-game">
    <h1>Create Your Own Solar System!</h1>

    <!-- Solar System Canvas -->
    <div
      ref="canvas"
      class="solar-system-canvas"
      @mousedown="startCanvasInteraction"
      @touchstart.prevent="startCanvasInteraction"
    >
      <div
        v-for="(planet, index) in placedPlanets"
        :key="index"
        class="placed-planet"
        :style="{ top: planet.y + 'px', left: planet.x + 'px' }"
        @click="speakOutLoud(planet.name)"
      >
        <img
          :src="planet.image"
          :alt="planet.name"
          draggable="false"
        >
      </div>
    </div>

    <!-- Instruction Message -->
    <p class="instruction-message">
      Drag and drop planets onto the canvas!
    </p>

    <!-- Planets List -->
    <div class="planets-list">
      <div
        v-for="(planet, index) in planets"
        :key="index"
        class="planet-item"
        @mousedown="onPress($event, planet, index)"
        @touchstart.prevent="onPress($event, planet, index)"
      >
        <img
          :src="planet.image"
          :alt="planet.name"
        >
        <span>{{ planet.name }}</span>
      </div>
    </div>

    <!-- Reset Button -->
    <button
      class="reset-button"
      @click="resetSolarSystem"
    >
      Reset Solar System
    </button>

    <!-- Finish Button -->
    <button
      v-if="planets.length === 0 && placedPlanets.length === initialPlanets.length"
      class="finish-button"
      @click="finishSolarSystem"
    >
      Finish
    </button>
  </div>
</template>

<script>

import { speechMixin } from "@/mixins/speechMixin.js";

import EarthImage from "@/assets/planets/Earth.png";

const initialPlanets = [
  {
    name: "Mercury",
    image:
      "https://png.pngtree.com/png-clipart/20230801/original/pngtree-mercury-planet-isolated-on-white-background-picture-image_7805941.png",
  },
  {
    name: "Venus",
    image:
      "https://images.vexels.com/media/users/3/205478/isolated/lists/4a42dba3e55a9bcc9764720d87f2ca93-planet-venus-illustration.png",
  },
  {
    name: "Earth",
    image: EarthImage,
  },
  {
    name: "Mars",
    image:
      "https://www.pngplay.com/wp-content/uploads/4/Mars-PNG-Images-HD.png",
  },
  {
    name: "Jupiter",
    image: "https://cdn-icons-png.flaticon.com/512/124/124558.png",
  },
  {
    name: "Saturn",
    image:
      "https://img.pikbest.com/origin/09/22/96/52MpIkbEsTJDS.png!sw800",
  },
  {
    name: "Uranus",
    image:
      "https://www.pngplay.com/wp-content/uploads/13/Uranus-PNG-HD-Quality.png",
  },
  {
    name: "Neptune",
    image:
      "https://static.vecteezy.com/system/resources/previews/020/004/117/non_2x/neptune-planet-illustration-png.png",
  },
];

export default {
  mixins: [speechMixin],
  data() {
    return {
      planets: [...initialPlanets],
      placedPlanets: [],
      draggingPlanet: null,
      draggingPlanetIndex: null,
      dragImage: null,
      selectedVoice: null,
      offsetX: 0,
      offsetY: 0,
      initialPlanets, // Keep reference for comparison
    };
  },
  mounted() {
    this.setupVoices();
  },
  methods: {
    onPress(event, planet, index) {
      event.preventDefault();

      // Determine if touch or mouse event
      let moveEvent = event.type === "touchstart" ? "touchmove" : "mousemove";
      let endEvent = event.type === "touchstart" ? "touchend" : "mouseup";

      this.draggingPlanet = planet;
      this.draggingPlanetIndex = index;

      // Record the offset between touch point and planet-item top-left corner
      const rect = event.currentTarget.getBoundingClientRect();
      let clientX, clientY;
      if (event.type.startsWith("touch")) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      this.offsetX = clientX - rect.left;
      this.offsetY = clientY - rect.top;

      // Create a clone of the planet image to drag
      this.createDragImage(event);

      // Add event listeners for move and end
      document.addEventListener(moveEvent, this.onDrag);
      document.addEventListener(endEvent, this.onRelease);
    },
    createDragImage(event) {
      // Clone the planet-item element
      const sourceElement = event.currentTarget;
      this.dragImage = sourceElement.cloneNode(true);
      this.dragImage.classList.add("drag-image");

      // Adjust styles
      this.dragImage.style.position = "fixed";
      this.dragImage.style.pointerEvents = "none";
      this.dragImage.style.margin = "0";
      this.dragImage.style.left = "0";
      this.dragImage.style.top = "0";
      this.dragImage.style.zIndex = "1000";
      this.dragImage.style.transform = "translate(0, 0)";
      this.dragImage.style.width = sourceElement.offsetWidth + "px";
      this.dragImage.style.height = sourceElement.offsetHeight + "px";

      // Position the image
      this.updateDragImagePosition(event);

      // Add to body
      document.body.appendChild(this.dragImage);
    },
    updateDragImagePosition(event) {
      let clientX, clientY;
      if (event.type.startsWith("touch")) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      const x = clientX - this.offsetX;
      const y = clientY - this.offsetY;

      this.dragImage.style.transform = `translate(${x}px, ${y}px)`;
    },
    onDrag(event) {
      event.preventDefault();
      this.updateDragImagePosition(event);
    },
    onRelease(event) {
      event.preventDefault();

      // Determine if touch or mouse event
      let moveEvent = event.type === "touchend" ? "touchmove" : "mousemove";
      let endEvent = event.type === "touchend" ? "touchend" : "mouseup";

      // Remove event listeners
      document.removeEventListener(moveEvent, this.onDrag);
      document.removeEventListener(endEvent, this.onRelease);

      // Remove drag image
      if (this.dragImage && this.dragImage.parentNode) {
        document.body.removeChild(this.dragImage);
        this.dragImage = null;
      }

      // Get drop position relative to canvas
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      let clientX, clientY;
      if (event.type.startsWith("touch")) {
        clientX = event.changedTouches[0].clientX;
        clientY = event.changedTouches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      const x = clientX - canvasRect.left - this.offsetX;
      const y = clientY - canvasRect.top - this.offsetY;

      // Check if dropped inside the canvas
      if (
        clientX >= canvasRect.left &&
        clientX <= canvasRect.right &&
        clientY >= canvasRect.top &&
        clientY <= canvasRect.bottom
      ) {
        this.placedPlanets.push({
          ...this.draggingPlanet,
          x: x,
          y: y,
        });

        // Remove the planet from the list
        this.planets.splice(this.draggingPlanetIndex, 1);

        this.speakOutLoud(this.draggingPlanet.name);
      }

      this.draggingPlanet = null;
      this.draggingPlanetIndex = null;
    },
    startCanvasInteraction(event) {
      // Prevent default behavior to avoid text selection
      event.preventDefault();
    },
    resetSolarSystem() {
      this.placedPlanets = [];
      this.planets = [...initialPlanets];
    },
    finishSolarSystem() {
      // Navigate to /planets route
      this.$router.push("/planets");
    },
  },
};
</script>

<style scoped>
.planetarium-game {
  text-align: center;
  margin: 20px;
  color: black;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 20px;
}

.solar-system-canvas {
  width: 80%;
  max-width: 800px;
  height: 400px;
  margin: 0 auto;
  background-image: url("https://static.vecteezy.com/system/resources/thumbnails/044/229/340/small_2x/starry-night-sky-space-seamless-pattern-galaxy-shiny-stars-background-fabric-seamless-print-or-textile-background-wrapping-paper-space-pattern-or-wallpaper-with-comet-glowing-constellations-vector.jpg");
  background-size: cover;
  background-position: center;
  position: relative;
  border: 2px solid #fff;
  border-radius: 10px;
  overflow: hidden;
  touch-action: none;
}

.placed-planet {
  position: absolute;
  width: 50px;
  height: 50px;
  cursor: pointer;
  pointer-events: auto;
}

.placed-planet img {
  width: 100%;
  height: 100%;
}

.instruction-message {
  font-size: 1.2rem;
  margin: 10px 0;
}

.planets-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 20px auto;
  max-width: 800px;
}

.planet-item {
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px;
  width: 100px;
  margin: 5px;
  cursor: grab;
  user-select: none;
  text-align: center;
  touch-action: none;
}

.planet-item img {
  width: 50px;
  height: 50px;
}

.planet-item span {
  display: block;
  margin-top: 5px;
  font-size: 1rem;
}

.reset-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 20px;
}

.reset-button:hover {
  background-color: #d32f2f;
}

.finish-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 20px;
}

.finish-button:hover {
  background-color: #388e3c;
}

.drag-image {
  position: fixed;
  pointer-events: none;
  z-index: 1000;
  /* Remove any transforms that interfere with precise positioning */
}

@media (max-width: 600px) {
  h1 {
    font-size: 2rem;
  }

  .instruction-message {
    font-size: 1rem;
  }

  .planets-list {
    width: 100%;
  }

  .planet-item {
    width: 80px;
  }
}
</style>